import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import PrintPage from "@components/print/PrintElements/PrintPage";
import { useAppSelector } from "@/store/hooks";
import { DocumentSummaryData, generateReportTotal, ReportTotalDetail } from "@utils/DocumentStatus";
import ReportPage, { ReportDataProps } from "@components/dynamic/ReportPage";
import moment from "moment";
import styles from "./style.module.scss";
import { CURRENCIES_CODES } from "@utils/Constants";
import { buildClientSettlementsReportRows } from "@utils/clientSettlementsReportRows";
import LoadingScreen from "@components/content/LoadingScreen";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";
import { CurrencyApi, CurrencyDataType } from "@/api/CurrencyApi";
import { SettlementApi, SettlementDataType } from "@/api/SettlementApi";

type ClientSettlementsReportPrintProps = {
  excelExport?: boolean
}

type SeparatedTotals = {
  taxable: DocumentSummaryData,
  nonTaxable: DocumentSummaryData
}

const ClientSettlementsReportPrint = (props: ClientSettlementsReportPrintProps) => {
  const { excelExport = false } = props
  const params = useParams()
  const { clientId, zoneId, startDate = "", endDate = "", excludeAnuladas = "0", autoPrint = "0" } = params;
  const suscription = useAppSelector((state) => { return state.suscription.suscription || {} });
  const [reportData, setReportData] = useState<ReportDataProps[]>([]);
  const [reportTotals, setReportTotals] = useState<ReportTotalDetail[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    import("@printerVariation/generic.scss");
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
      loadReportData()
    }
  }, [startDate, endDate, excelExport, excludeAnuladas]);

  const addSettlementToSeparatedTotals = (settlement: SettlementDataType, totals: Record<string, SeparatedTotals>): Record<string, SeparatedTotals> => {
    if (!!settlement.anulada) return totals;
    const key = `${settlement.currencyId}`;
    const current = totals[key] || {
      taxable: { subtotal: 0, descuento: 0, impuestos: 0, total: 0 },
      nonTaxable: { subtotal: 0, descuento: 0, impuestos: 0, total: 0 },
    };
    current.taxable.subtotal += Number(settlement.subtotal || 0);
    current.taxable.descuento += Number(settlement.descuento || 0);
    current.taxable.impuestos += Number(settlement.impuestos || 0);
    current.taxable.total += Number(settlement.total || 0);
    return { ...totals, [key]: current };
  };

  const renderTotalEntry = (summary: DocumentSummaryData, currencyCode: string): ReportTotalDetail[] => {
    return [
      { title: `Subtotal`, total: summary.subtotal, currencyCode },
      { title: `Descuento`, total: summary.descuento, currencyCode },
      { title: `Impuestos`, total: summary.impuestos, currencyCode },
      { title: `Total`, total: summary.total, currencyCode },
    ]
  }

  const generateSeparatedTotalLines = (separatedTotals: Record<string, SeparatedTotals>, currencies: CurrencyDataType[]): ReportTotalDetail[] => {
    const totals = Object.entries(separatedTotals).flatMap(([key, value]) => {
      const currency = currencies.find(c => c.id == key);
      const currencyId = currency?.currency_id || 1
      return [
        { title: `Detalle Liquidaciones en ${CURRENCIES_CODES[currencyId]}` },
        { title: `separator`, separatorLine: true },
        { title: `Total Liquidaciones Gravadas en ${CURRENCIES_CODES[currencyId]}` },
        ...(renderTotalEntry(value.taxable, CURRENCIES_CODES[currencyId])),
        { title: "separator", separatorLine: true },
      ]
    })
    return totals
  }

  const loadReportData = async () => {
    setIsLoading(true);
    try {
      let filters = {}
      if (clientId && clientId != "-2") {
        filters = {
          clientId: { eq: clientId }
        }
      }
      if (zoneId && zoneId != "-2") {
        filters = {
          ...filters,
          client: { zoneId: { eq: zoneId } }
        }
      }

      const { items: currencies } = await CurrencyApi.fetchAll();
      const { items: rawSettlements } = await SettlementApi.fetchAllByPeriod(startDate, endDate, filters, "", 10000)

      const shouldExcludeAnuladas = excludeAnuladas === "1";
      const settlements = shouldExcludeAnuladas
        ? rawSettlements.filter((s) => !s.anulada)
        : rawSettlements;

      let separatedTotals: Record<string, SeparatedTotals> = {}
      const currenciesOnReport = new Set<string>();

      settlements.forEach((s) => {
        separatedTotals = addSettlementToSeparatedTotals(s, separatedTotals);
        if (s.currency?.currency_id) {
          currenciesOnReport.add(CURRENCIES_CODES[Number(s.currency.currency_id) || 1]);
        }
      });

      const reportRows = buildClientSettlementsReportRows(settlements, {
        anulada: styles.anulada,
        productDetail: styles.productDetail,
      });

      const separatedTotalData = generateSeparatedTotalLines(separatedTotals, currencies);
      const reportTotal = generateReportTotal(reportRows, [
        { dataColumnIndex: 4, title: "Subtotal Liquidaciones" },
        { dataColumnIndex: 5, title: "Total Descuento" },
        { dataColumnIndex: 6, title: "Total IVA" },
        { dataColumnIndex: 7, title: "Total Liquidaciones" },
      ], currenciesOnReport, 8);

      setReportData(reportRows);
      setReportTotals([
        ...separatedTotalData,
        ...reportTotal,
      ]);
    } finally {
      setIsLoading(false);
      PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
    }
  }

  const reportTitles = useMemo(() => {
    return [
      { label: "# Liquidación", width: "10%" },
      { label: "Fecha", width: "10%" },
      { label: "Cliente", width: "25%" },
      { label: "Anulada", width: "5%" },
      { label: "Subtotal", width: "10%", isNumeric: true },
      { label: "Descuento", width: "10%", isNumeric: true },
      { label: "Iva", width: "10%", isNumeric: true },
      { label: "Total", width: "10%", isNumeric: true },
      { label: "Moneda", width: "5%" },
      { label: "Tipo de Cambio", width: "5%", isNumeric: true },
      { label: `${suscription.reference_label || "# Referencia"}`, width: "0%", hideFromPdf: true },
    ];
  }, [suscription.reference_label]);

  return (
    <div>
      <PrintPage
        autoPrint={autoPrint === "1"}
        onReadyDebounce={2000}
      >
        <ReportPage
          excelExport={excelExport}
          loading={isLoading}
          suscription={suscription}
          reportConfig={{
            title: "Reporte de Liquidaciones",
            date: `${moment().format()}`,
          }}
          reportTotals={reportTotals}
          reportData={reportData}
          reportTitles={reportTitles}
        />
      </PrintPage>
      <LoadingScreen isLoading={isLoading} />
    </div>
  );
}

export default ClientSettlementsReportPrint;
